import { createFileRoute, redirect, useLocation, useSearch } from "@tanstack/react-router";
import { AdministratorsTable } from "./-components/administrator-accounts-table";
import { useContext, useEffect, useMemo } from "react";
import { DashboardContext } from "@/components/dashboard-layout";
import { AdministratorDetails } from "./-components/administrator-accounts-sidebar";
import { useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { administratorAccountsQueryOptions, administratorProfilesQueryOptions } from "@/server/administrators";
import { toast } from "sonner";
import { useRightSidebarClose } from "@/hooks/use-right-sidebar-close";
import { useRightSidebarOpen } from "@/hooks/use-right-sidebar-open";
import { CrudEntry } from "@/lib/mma/types.gen";
import {
  checkIfUserMayRead,
  ContextUser,
  checkIfUserMayWrite,
  checkIfUserMayDelete,
  getNextRedirectRoute,
} from "@/lib/utils";

export const Route = createFileRoute("/_dashboard/administrators/accounts")({
  component: RouteComponent,
  beforeLoad: ({ context }) => {
    const user = context.user as ContextUser;
    const mayRead = checkIfUserMayRead(user?.profile, user?.accessControl, "ADMIN_USER");

    if (!mayRead) {
      const nextRedirectRoute = getNextRedirectRoute(user);
      throw redirect({ to: nextRedirectRoute });
    }
  },
  loader: ({ context }) => {
    return {
      crumb: "Administrator accounts",
      currentUsername: (context as { user?: { username: string } }).user?.username,
      userProfileId: (context as { user?: { profile: string } }).user?.profile,
      accessControl: (context as { user?: { accessControl: Record<string, CrudEntry> } }).user?.accessControl,
    };
  },
});

function RouteComponent() {
  const navigate = useNavigate();
  const location = useLocation();
  const { accessControl, userProfileId, currentUsername } = Route.useLoaderData();
  //If the user has no profile, it means they have access to everything. Profiles are restrictive only.
  const mayWrite = checkIfUserMayWrite(userProfileId, accessControl, "ADMIN_USER");
  const mayDelete = checkIfUserMayDelete(userProfileId, accessControl, "ADMIN_USER");
  const searchParams = useSearch({ strict: false }) as {
    id?: string;
  };
  const context = useContext(DashboardContext);
  const closeRightSidebar = useRightSidebarClose();
  const openRightSidebar = useRightSidebarOpen();

  const administratorAccountsQuery = useQuery(administratorAccountsQueryOptions);
  const administrators = administratorAccountsQuery.data || [];
  const administratorProfilesQuery = useQuery(administratorProfilesQueryOptions);
  const profiles = administratorProfilesQuery.data || [];

  const isExact = location.href === "/administrators/accounts" || location.href === "/administrators/accounts/";

  // Build sidebar component based on searchParams
  const sidebarComponent = useMemo(() => {
    if (!searchParams.id) return null;
    return (
      <AdministratorDetails
        administratorId={searchParams.id === "new" ? null : searchParams.id}
        currentUsername={currentUsername}
      />
    );
  }, [searchParams.id, currentUsername]);

  useEffect(() => {
    if (administratorAccountsQuery.isError) {
      const message =
        administratorAccountsQuery.error instanceof Error
          ? administratorAccountsQuery.error.message
          : "Failed to load administrator accounts. Please try again later.";
      toast.error(message);
    }
  }, [administratorAccountsQuery.isError, administratorAccountsQuery.error]);

  useEffect(() => {
    if (administratorProfilesQuery.isError) {
      const message =
        administratorProfilesQuery.error instanceof Error
          ? administratorProfilesQuery.error.message
          : "Failed to load administrator profiles. Please try again later.";
      toast.error(message);
    }
  }, [administratorProfilesQuery.isError, administratorProfilesQuery.error]);

  // Check if context is available
  if (!context) {
    console.error("DashboardContext is null. Ensure DashboardLayout wraps this component.");
    return <div>Error: DashboardContext is not available.</div>;
  }

  // Handle sidebar opening/closing based on search params
  useEffect(() => {
    if (!context) {
      return;
    }

    const shouldClose = isExact && !!context?.rightSidebar;
    const hasPermission = searchParams.id !== "new" || mayWrite;

    openRightSidebar({
      component: sidebarComponent,
      paramKeys: "id",
      shouldClose,
      permissionCheck: !hasPermission,
      onPermissionDenied: () => closeRightSidebar("id"),
    });
  }, [searchParams.id, sidebarComponent, mayWrite, isExact, openRightSidebar, closeRightSidebar]);

  const handleAddAdmin = () => {
    navigate({
      to: "/administrators/accounts",
      replace: true,
      search: (prev) => ({ ...prev, id: "new" }),
    });
  };

  return (
    <AdministratorsTable
      administrators={administrators}
      handleAddAdmin={handleAddAdmin}
      isLoading={administratorAccountsQuery.isLoading || administratorProfilesQuery.isLoading}
      currentUsername={currentUsername}
      mayWrite={mayWrite}
      mayDelete={mayDelete}
    />
  );
}
